import React, { useState, useContext, useEffect, useCallback } from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import TermsAndConditionsModal from "terms_and_conditions/TermsAndConditionsModal";
import { debounce } from "lodash";
import showAlert from 'utils/showAlert';

export default function RegisterAccount(props) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isThankYouModalVisible, setIsThankYouModalVisible] = useState(false);
  const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);
  const [newUser, setNewUser] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirmation: ''
  });

  const showModal = () => setIsModalVisible(true);
  const hideModal = () => setIsModalVisible(false);
  const showThankYouModal = () => setIsThankYouModalVisible(true);
  const hideThankYouModal = () => setIsThankYouModalVisible(false);
  const showTermsModal = () => setIsTermsModalVisible(true);
  const hideTermsModal = () => setIsTermsModalVisible(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Add New User Functionality
  const handleNewUserChange = (event) => {
    event.preventDefault();
    const { name, value } = event.target;
    setNewUser({ ...newUser, [name]: value });
  };

  const createNewUser = async (event) => {
    event.preventDefault();
    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    let hasError = false;
    const userToAdd = newUser;
    const firstName = userToAdd.first_name.trim();
    const lastName = userToAdd.last_name.trim();
    const email = userToAdd.email.trim();
    const password = userToAdd.password.trim();
    const passwordConfirmation = userToAdd.password_confirmation.trim();
  
    if (!firstName) {
      hasError = true;
      document.getElementById("new-user-first-name").classList.add("lite-invalid-input");
    } else {
      document.getElementById("new-user-first-name").classList.remove("lite-invalid-input");
    }
    if (!lastName) {
      hasError = true;
      document.getElementById("new-user-last-name").classList.add("lite-invalid-input");
    } else {
      document.getElementById("new-user-last-name").classList.remove("lite-invalid-input");
    }
    if (!isValidEmail(email)) {
      hasError = true;
      document.getElementById("new-user-email").classList.add("lite-invalid-input");
    } else {
      document.getElementById("new-user-email").classList.remove("lite-invalid-input");
    }
    if (!password) {
      hasError = true;
      document.getElementById("new-user-password").classList.add("lite-invalid-input");
    }
    if (!passwordConfirmation) {
      hasError = true;
      document.getElementById("new-user-password-confirmation").classList.add("lite-invalid-input");
    }
    if (password !== passwordConfirmation) {
      hasError = true;
      document.getElementById("new-user-password").classList.add("lite-invalid-input");
      document.getElementById("new-user-password-confirmation").classList.add("lite-invalid-input");
    }
  
    if (!hasError) {
      try {
        // Send request to create new user and wait for result
        const success = await createUserRequest(userToAdd);
  
        if (success) {
          if (typeof gtag === "function") { // gtag is only defined on production environment
            gtag("event", "datapulse_lead", {
              event_label: "datapulse_account_register",
            })
          }

          hideModal();
          showThankYouModal();
        }
      } catch (error) {
        console.error("Error creating user:", error);
      }
    }
  };
  
  const createUserRequest = async (userToAdd) => {
    console.log("Creating new user", userToAdd);
    const authenticityToken = document.querySelector('meta[name="csrf-token"]').content;
    
    try {
      const response = await fetch('/datapulse/profile', { // routes to users/registrations#create
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': authenticityToken,
        },
        body: JSON.stringify({
          user: userToAdd
        })
      });
  
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('New user created successfully', data);
          return true;
        } else {
          console.error('Failed to create new user', data.error_message);
          showAlert(`Failed to register your account. ${data.error_message}`, 'danger');
          return false;
        }
      } else {
        console.error('Failed to create new user');
        showAlert('Error - Failed to register your account.', 'danger');
        return false;
      }
    } catch (error) {
      console.error('Request error:', error);
      showAlert('An error occurred while trying to register your account.', 'danger');
      return false;
    }
  };

  return (
    <>
      <div className="create-new-account">
        <p>
          Don't have an account?
          <a onClick={showModal} style={{marginLeft: "4px", cursor: "pointer"}}>Create one</a>
        </p>
      </div>
      <Modal className="lite-modal-2 register-account-modal" show={isModalVisible} onHide={hideModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <h4>Create your account</h4>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Thanks for your interest in DataPulse! Please create your free account to continue.</p>
          <div className="d-flex">
            <div className="flex-fill">
              <div className="new-user-form d-flex">
                <div className="basic-information">
                  <div className="lite-form-group mb-3">
                    <label className="input-label" htmlFor="new-user-first-name">First Name</label>
                    <input className="lite-input" type="text" id="new-user-first-name" name="first_name" value={newUser.first_name} onChange={handleNewUserChange} />
                  </div>
                  <div className="lite-form-group mb-3">
                    <label className="input-label" htmlFor="new-user-last-name">Last Name</label>
                    <input className="lite-input" type="text" id="new-user-last-name" name="last_name" value={newUser.last_name} onChange={handleNewUserChange} />
                  </div>
                  <div className="lite-form-group mb-3">
                    <label className="input-label" htmlFor="new-user-email">Email Address</label>
                    <input className="lite-input" type="email" id="new-user-email" name="email" value={newUser.email} onChange={handleNewUserChange} />
                  </div>
                  <hr className="lite-hr" />
                  <div className="lite-form-group mb-3">
                    <label className="input-label" htmlFor="new-user-password">Password<small><i>{" ("}minimum 8 characters{")"}</i></small></label>
                    <input className="lite-input" type="password" id="new-user-password" name="password" value={newUser.password} onChange={handleNewUserChange} />
                  </div>
                  <div className="lite-form-group mb-3">
                    <label className="input-label" htmlFor="new-user-password-confirmation">Confirm Password</label>
                    <input className="lite-input" type="password" id="new-user-password-confirmation" name="password_confirmation" value={newUser.password_confirmation} onChange={handleNewUserChange} />
                  </div>
                </div>
              </div>
              <div className="d-flex justify-content-end">
                <div className="lite-checkbox-container">
                  <span className="d-inline-block">I accept the <a className="terms-and-conditions-link" onClick={() => showTermsModal()}>Terms and Conditions</a></span>
                  <input type="checkbox" id="accept-terms-checkbox" name="accept-terms-checkbox" className="lite-checkbox"  onChange={() => setAcceptedTerms(!acceptedTerms)} checked={acceptedTerms} />
                </div>
              </div>
              <div className="d-flex justify-content-end">
                <button className={`button b-type-primary b-size-small b-color-green mt-3 ${!acceptedTerms && "disabled"}`} id="new-account-register" onClick={(e) => createNewUser(e)} disabled={!acceptedTerms}>
                  Register
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal className="lite-modal-2 thank-you-for-registering-modal" show={isThankYouModalVisible} onHide={hideThankYouModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <h4>Confirm your account</h4>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">Welcome to DataPulse! Your account is created, we just need you to confirm your email address.</p>
          <div className="confirm-instructions">
            <img src={`/datapulse${require("images/lite/icons/email.png")}`} className="filter-green" />
            <p>Check for your <b>confirmation email</b> from support@digitalposition.com and click the link. (Be sure to check your spam folder)</p>
          </div>
        </Modal.Body>
      </Modal>

      <TermsAndConditionsModal show={isTermsModalVisible} hideTermsModal={hideTermsModal} acceptedTerms={acceptedTerms} setAcceptedTerms={setAcceptedTerms} />
    </>
  );
}